import { useGetRevisionsQuery } from '@carboncopyco/data';
import type { Revision } from '@carboncopyco/types';
import {
  Container,
  SkeletonRowLoading,
  TextBase,
  TextButton,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import { type Col, FixedTable } from '../../../FixedTable';
import { CreatedOnTooltip } from '../../CreatedOnTooltip';
import { TableRowWrapper } from '../../TableRowWrapper';
import {
  type OverlayPageState,
  useOverlayPageProps,
} from '../OverlayPageContext';
import { RevertButton } from './RevertButton';

const cols: Col<Revision>[] = [
  {
    title: 'Version',
    width: 192,
    render: (revision) => (
      <div className="flex flex-col">
        <TextButton
          ellipsis
          label={`Version ${revision.currRevision + 1}`}
          color="text-ds-natural-600"
        />
      </div>
    ),
  },
  {
    title: 'Date',
    width: 160,
    render: ({ createTime }) => <CreatedOnTooltip dateTime={createTime} />,
  },
  {
    title: 'Updated by',
    width: 168,
    render: (revision) => (
      <UserBasicInfo
        user={revision.creator}
        truncateWidth={115}
        textColor="text-ds-natural-600"
        tooltip={revision.creator.name.length > 13}
        size="small"
      />
    ),
  },
  {
    title: 'Description',
    width: 390,
    maxContentWidth: 340,
    render: (revision) => (
      <TooltipBubble
        placement="top-start"
        title={revision.commit}
      >
        <div className="truncate text-ds-natural-600">
          <TextBase
            label={revision.commit}
            color="text-ds-natural-600"
          />
        </div>
      </TooltipBubble>
    ),
  },
  {
    title: '',
    width: 70,
    render: (revision) => (
      <TooltipBubble
        title="Revert"
        placement="top"
      >
        <RevertButton revision={revision} />
      </TooltipBubble>
    ),
  },
];

export const RevisionsTable = () => {
  const { organizationId, demoId, readOnly }: OverlayPageState =
    useOverlayPageProps();
  const {
    data: revisions,
    isLoading,
    error,
  } = useGetRevisionsQuery({
    organizationId,
    sourceType: 'demo',
    id: demoId,
    reverseOrder: true,
    limit: 100,
  });

  return (
    <div className="py-5">
      <FixedTable<Revision>
        sideSpacing
        rowWrapper={TableRowWrapper}
        cols={readOnly ? cols.slice(0, -1) : cols}
        data={revisions?.items ?? []}
        infiniteListMaxHeight={600}
        isLoading={isLoading}
        error={error}
        loader={
          <Container>
            <div className="w-ds-lg-modal">
              <SkeletonRowLoading
                medium
                itemsCount={5}
              />
            </div>
          </Container>
        }
      />
    </div>
  );
};
